import { useEffect, useId, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/state/AuthContext";
import { BrandWordmark } from "@/components/BrandWordmark";
import { startTransitionActivity } from "@/lib/networkActivity";

function BunnyMark() {
  return (
    <span className="lp-brand-mark">
      <svg width="45" height="45" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 11.5c0-3.5 1.2-7 1.8-7.2.6-.2 1 .8 1.2 2 .2 1.2.2 2.7.2 4.2" stroke="#2B1F3D" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M16 11.5c0-3.5-1.2-7-1.8-7.2-.6-.2-1 .8-1.2 2-.2 1.2-.2 2.7-.2 4.2" stroke="#2B1F3D" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M5.5 16.5c0-3.2 2.9-5.5 6.5-5.5s6.5 2.3 6.5 5.5c0 2.6-2.4 3.5-6.5 3.5s-6.5-.9-6.5-3.5z" stroke="#2B1F3D" strokeWidth="1.4"/>
        <circle cx="10" cy="16.4" r=".7" fill="#2B1F3D"/>
        <circle cx="14" cy="16.4" r=".7" fill="#2B1F3D"/>
      </svg>
    </span>
  );
}

interface LandingNavProps {
  onCreateLink: () => void;
}

export function LandingNav({ onCreateLink: _onCreateLink }: LandingNavProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /**
   * The section links are in-page anchors, which only resolve on the landing
   * page — the only route that renders #how, #workflow and #integrations.
   *
   * On any other route (/pricing, /privacy, /terms, /contact, /docs/zoom) a bare
   * "#how" matches nothing and the link silently does nothing at all. Prefixing
   * with "/" turns it into a real navigation to the landing page, which then
   * scrolls to the section.
   *
   * Kept as a bare hash on the landing page itself so the existing behaviour is
   * untouched: smooth in-page scroll, no route change, and the scroll-spy
   * underline in useLandingMotion (which only tracks hrefs starting with "#")
   * keeps working.
   */
  const isLanding = pathname === "/";
  const anchor = (hash: string) => (isLanding ? hash : `/${hash}`);

  const handleSignInClick = () => {
    startTransitionActivity("sign-in-route");
    navigate("/sign-in?mode=APP_LOGIN", { state: { preloadSignIn: true } });
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const closeMenu = () => setMenuOpen(false);

  // Close on route change and on Escape. In-page anchors close via onClick,
  // but a route navigation (Pricing from /contact, say) does not re-mount here.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <nav className="lp-nav" aria-label="Primary" data-nav>
      <div className="lp-nav-inner" data-nav-inner>
        {/* The brand mark is "go home". On the landing page that means scrolling
            back to the top; anywhere else it has to be a real link to "/", which
            is what people expect from a logo. */}
        {isLanding ? (
          <a href="#top" className="lp-brand">
            <BunnyMark />
            <BrandWordmark style={{ fontFamily: "var(--lp-sans)", fontWeight: 600 }} />
          </a>
        ) : (
          <Link to="/" className="lp-brand">
            <BunnyMark />
            <BrandWordmark style={{ fontFamily: "var(--lp-sans)", fontWeight: 600 }} />
          </Link>
        )}
        <div className="lp-nav-links">
          <a className="lp-navlink" data-navlink href={anchor("#how")}>How it works</a>
          <a className="lp-navlink" data-navlink href={anchor("#workflow")}>Workflow</a>
          <a className="lp-navlink" data-navlink href={anchor("#integrations")}>Integrations</a>
          {/* Pricing is a real page now, so it's a route everywhere rather than
              an in-page anchor. On the landing page it still scrolls to the
              #pricing section rather than navigating away. */}
          <a className="lp-navlink" data-navlink href={isLanding ? "#pricing" : "/pricing"}>Pricing</a>
          {/* Docs and Blog are static Hugo sections served from the same origin,
              not React routes — plain <a> so the browser does a real navigation
              instead of the router trying to match a path it doesn't own. The
              nav partial in site/themes mirrors this list.

              No `data-navlink`: that attribute marks in-page scroll-spy targets,
              and these hrefs are paths, not selectors. */}
          <a className="lp-navlink" href="/docs/">Docs</a>
          <a className="lp-navlink" href="/blog/">Blog</a>
        </div>
        <div className="lp-nav-cta">
          <div className="lp-nav-account">
            {user ? (
              <Link to="/dashboard" className="lp-btn lp-btn-ghost lp-btn-sm">Meetings</Link>
            ) : (
              // One button, both purposes. Auth is OAuth-only and the backend upserts, so there is
              // no separate sign-up door to point at — labelling this "Sign in" alone read as
              // members-only to visitors who do not have an account yet.
              <button type="button" className="lp-btn lp-btn-primary lp-btn-sm" onClick={handleSignInClick}>Sign in / Sign up</button>
            )}
          </div>
          {/* Below md the link row is hidden; this reveals the same destinations
              in a drawer. Without it the marketing nav had no menu at all. */}
          <button
            type="button"
            className="lp-nav-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className={`lp-nav-toggle-bars${menuOpen ? " open" : ""}`} aria-hidden="true">
              <span />
              <span />
            </span>
          </button>
        </div>
      </div>

      {/* Full-screen scrim behind the drawer. It blurs and dims the page so the
          menu is clearly the only thing in focus, and it doubles as the
          tap-anywhere-to-close target that a drawer is expected to have.
          aria-hidden + no tab stop: Escape and the toggle already close the
          menu, so this adds nothing for keyboard or screen-reader users. */}
      {menuOpen && (
        <div
          className="lp-nav-scrim"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {menuOpen && (
        <div id={menuId} className="lp-nav-menu">
          <a className="lp-nav-menu-link" href={anchor("#how")} onClick={closeMenu}>How it works</a>
          <a className="lp-nav-menu-link" href={anchor("#workflow")} onClick={closeMenu}>Workflow</a>
          <a className="lp-nav-menu-link" href={anchor("#integrations")} onClick={closeMenu}>Integrations</a>
          <a className="lp-nav-menu-link" href={isLanding ? "#pricing" : "/pricing"} onClick={closeMenu}>Pricing</a>
          <a className="lp-nav-menu-link" href="/docs/">Docs</a>
          <a className="lp-nav-menu-link" href="/blog/">Blog</a>
          {user ? (
            <Link to="/dashboard" className="lp-btn lp-btn-ghost lp-nav-menu-account" onClick={closeMenu}>
              Meetings
            </Link>
          ) : (
            <button
              type="button"
              className="lp-btn lp-btn-primary lp-nav-menu-account"
              onClick={() => {
                closeMenu();
                handleSignInClick();
              }}
            >
              Sign in
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
